import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router";

import { ApiHelper } from "@datanac/datanac-api-toolkit/dist/api-helper";
import { useDialog } from "@datanac/datanac-mui-toolkit";
import { Refresh } from "@mui/icons-material";
import EditNoteOutlined from "@mui/icons-material/EditNoteOutlined";
import UploadFileOutlined from "@mui/icons-material/UploadFileOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import { Box, Dialog, Divider, IconButton, Paper, Stack, Tooltip, Typography, useMediaQuery } from "@mui/material";
import Button from "@mui/material/Button";
import { GridActionsCellItem } from "@mui/x-data-grid";
import { downloadDocument, downloadDocumentByUrl, getFileIcon, getFileTypeName } from "api-toolkit/FilesHelper";
import { _sessionManager, UsersApiHelper } from "api/ApiHelper";
import { AppContext } from "App";
import { StatefulControlledTextField } from "components/Core/StatefulControlledTextField";
import { FarmVaultIcon } from "components/Icons/Icons";
import TilleyDataGrid from "components/shared/DataGrid/TilleyDataGrid";
import SkeletonTable from "components/shared/table-skeleton/table-skeleton";
import {
  DOCUMENT_SOURCE_EMAIL_DOMAIN,
  DOCUMENT_SOURCE_SECRET_THRESHOLD_DAYS,
} from "components/Vault/DocumentSource/DocumentSourceHelper";
import ImportUploadFileDialog from "components/Vault/Farm/components/Import/ImportUploadFileDialog";
import { WORKSPACE_VIEW_CARD, WORKSPACE_VIEW_TABLE } from "components/Workspaces/helpers";
import WorkspaceActionBar from "components/Workspaces/WorkspaceActionBar";
import WorkspaceHeader from "components/Workspaces/WorkspaceHeader";
import WorkspacePageHeader from "components/Workspaces/WorkspacePageHeader";
import _ from "lodash";
import PopupState from "material-ui-popup-state";
import { ButtonLargeSquare } from "mui-toolkit/ButtonLargeSquare";
import { useSnackbar } from "notistack";
import { formatDateContext, formatProducerName } from "utility/formatters";
import { v4 } from "uuid";

import { getTruncatedFilename } from "../../api-toolkit/FilesHelper";
import { useChatAssistantAttachments } from "../../contexts/useChatAssistantAttachments";
import DropdownSelector from "../DropdownSelector";
import { CopyIcon, DangerIcon, DeleteIcon, DownloadIcon, TimerIcon } from "../Icons/Icons";
import { DOCUMENT_SYSTEM_PROMPT_NAME_SEARCH, DocumentChatAssistant } from "./DocumentChatAssistant";
import FinancialDocumentsWorkspaceTable from "./FinancialDocumentsWorkspaceTable";
import { FINANCIAL_DOCUMENTS, FINANCIAL_DOCUMENTS_HAVE_TEMPLATES, TEMPLATE_URL_PATH } from "./FinancialHelpers";

const getRowHeightAuto = () => "auto";

export default function FinancialDocumentsWorkspace() {
  const { globalState, globalDispatch } = useContext(AppContext);

  const { pathname } = useLocation();

  const isSmall = useMediaQuery((theme) => theme.breakpoints.down("md"), { noSsr: true });
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("lg"), { noSsr: true });
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState(true);
  const [isloadDocumentSourceEntriesLoading, setIsloadDocumentSourceEntriesLoading] = useState(true);
  const { addAttachments } = useChatAssistantAttachments();
  const { component: confirmDialogComponent, actions: confirmDialogActions } = useDialog();

  const [currentYear, setCurrentYear] = useState(globalState?.crop_year);

  const documentFilters = ["All", "Production", "Financials", "Know Your Customer KYC"];
  const [currentDocumentFilter, setCurrentDocumentFilter] = useState(documentFilters[0]);
  const [workspaceMode, setWorkspaceMode] = useState(isSmall ? WORKSPACE_VIEW_CARD : WORKSPACE_VIEW_TABLE);

  useEffect(() => {
    if (pathname === "/vault/documents") {
      setCurrentDocumentFilter(documentFilters[0]);
    } else if (pathname === "/financial/documents") {
      setCurrentDocumentFilter(documentFilters[2]);
    }
  }, [pathname]);

  useEffect(() => {
    globalDispatch({
      type: "update",
      payload: {
        key: "crop_year",
        value: currentYear,
      },
    });
  }, [currentYear]);

  // ---

  const [activeProducer, setActiveProducer] = useState();
  const selectActiveProducer = async () => {
    let _producer = await UsersApiHelper.users_selectObject(
      "Member",
      { producer_token: globalState.producer_token },
      globalState
    );

    if (_producer?.length) {
      setActiveProducer(_producer[0]);
    }
  };
  useEffect(() => {
    if (globalState?.producer_token) {
      selectActiveProducer();
    } else {
      setActiveProducer(null);
    }
  }, [globalState?.producer_token]);

  // --- --- ---

  const [documents, setDocuments] = useState(null);
  const loadDocuments = async () => {
    try {
      setIsLoading(true);

      const _documentsFilter = {
        year: currentYear,
        is_active: true,
      };
      const _documents = await UsersApiHelper.users_selectObject("FinancialDocument", _documentsFilter, globalState);
      if (_documents?.length) {
        setDocuments(_documents);
      } else {
        setDocuments(null);
      }
      setIsLoading(false);
    } catch (err) {
      console.warn(err);
      enqueueSnackbar(err.detail || err.message || JSON.stringify(err), { preventDuplicate: true, variant: "error" });
    }
  };

  useEffect(() => {
    if (currentYear && globalState?.producer_token) {
      loadDocuments();
    }
  }, [currentYear, globalState?.producer_token]);

  // ---

  const [isUploadFileDialogOpen, setIsUploadFileDialogOpen] = useState(false);

  const handleDownloadClick = async (_financialDocument) => {
    try {
      await downloadDocument(_financialDocument, globalState).then((isSuccess) => {
        if (!isSuccess) {
          enqueueSnackbar("There is no file available for this document.", { preventDuplicate: true, variant: "info" });
        }
      });
    } catch (err) {
      console.warn(err);
      enqueueSnackbar(err?.detail || err?.message || err, { preventDuplicate: true, variant: "error" });
    }
  };

  const getDocumentByDocumentSourceEntry = useCallback(
    (_documentSourceEntry) => {
      const _doc = documents?.find((doc) => doc.id === _documentSourceEntry.id);
      return _doc;
    },
    [documents]
  );

  const getFileIcon_DocumentSourceEntry = useCallback(
    (_documentSourceEntry) => {
      const _financialDocument = getDocumentByDocumentSourceEntry(_documentSourceEntry);
      const icon = getFileIcon(_financialDocument);
      if (icon.props.sx.fontSize) {
        icon.props.sx.fontSize = "2rem";
      }
      return icon;
    },
    [documents]
  );

  const handleDownloadClick_DocumentSource = async (_documentSourceEntry) => {
    const _financialDocument = getDocumentByDocumentSourceEntry(_documentSourceEntry);
    return await handleDownloadClick(_financialDocument);
  };

  const handlePublicDownloadClick = async (_publicDocument) => {
    const urlResponse = await UsersApiHelper.getPublicDocument(
      {
        document_path: TEMPLATE_URL_PATH,
        document_name: _publicDocument?.templateFileName,
      },
      globalState
    );

    if (urlResponse?.url) {
      try {
        await downloadDocumentByUrl(urlResponse?.url, _publicDocument);
      } catch (err) {
        console.warn(err);
        enqueueSnackbar(err?.detail || err?.message || err, { preventDuplicate: true, variant: "error" });
      }
    }
  };

  const handleDeleteDocumentClick = async (_financialDocument) => {
    confirmDialogActions.confirm({
      title: "Delete this document?",
      prompt: "Are you sure you want to delete this document?",
      promptDetail: null,
      callback: () => {
        handleDeleteDocumentClick_Intern(_financialDocument);
      },
    });
  };

  const handleDeleteDocumentClick_Intern = async (_financialDocument) => {
    try {
      _financialDocument.is_active = false;
      await UsersApiHelper.updateFinancialDocument(_financialDocument, globalState);
      loadDocuments();
    } catch (err) {
      console.warn(err);
      enqueueSnackbar(err?.detail || err?.message || err, { preventDuplicate: true, variant: "error" });
    }
  };

  // ---
  // TODO this is not used
  // const [chatMessages, setChatMessages] = useState({
  //   chatTimestamp: Date(),
  //   messages: [],
  // });
  // const sendChat = async (text) => {
  //   const _chatMessages = chatMessages?.messages;
  //   _chatMessages.push({ message: text, direction: "outgoing" });
  //   setChatMessages({
  //     chatTimestamp: Date(),
  //     messages: _chatMessages,
  //   });

  //   UsersApiHelper.sendChatRequest({ text }, globalState).then((chatResponse) => {
  //     const _chatMessages = chatMessages?.messages;
  //     _chatMessages.push({ message: chatResponse, direction: "incoming" });
  //     setChatMessages({
  //       chatTimestamp: Date(),
  //       messages: _chatMessages,
  //     });
  //   });
  // };

  const handleError = (err) => {
    console.warn(err);
    enqueueSnackbar(err.detail || err.message || JSON.stringify(err), { preventDuplicate: true, variant: "error" });
  };

  const handleCopy = async (name, secret) => {
    try {
      // If the secret is specified name+secret@; else, just name@
      const emailAddress = (secret ? `${name}+${secret}@` : `${name}@`) + DOCUMENT_SOURCE_EMAIL_DOMAIN;
      navigator.clipboard.writeText(emailAddress);

      const message = `The email address ${emailAddress} has been copied to your clipboard.`;
      enqueueSnackbar(message, { preventDuplicate: true, variant: "info" });
    } catch (err) {
      handleError(err);
    }
  };

  const [documentSources, setDocumentSources] = useState(null);
  const loadDocumentSources = async () => {
    try {
      setIsLoading(true);

      const _documentsFilter = {
        producer_token: globalState?.producer_token,
      };
      const _documents = await UsersApiHelper.users_search("DocumentSource", _documentsFilter, globalState);

      if (_documents?.length) {
        setDocumentSources(_documents);
      } else if (globalState?.producer_token && activeProducer) {
        // Create new address:

        const _userName = formatProducerName(activeProducer) || null;
        // Format user name:
        // - Only take chars before the @ (if exists)
        // - Remove all non-alphanumeric chars
        let _formattedUserName = _userName;
        if (_formattedUserName) {
          const _atIndex = _formattedUserName.indexOf("@");
          if (_atIndex > 0) {
            _formattedUserName = _formattedUserName.substring(0, _atIndex);
          }
          _formattedUserName = _formattedUserName.replace(/[^a-zA-Z0-9]/g, "");
        }

        const _newDocumentSource = {
          id: v4(),
          producer_token: globalState?.producer_token,
          name: _formattedUserName,
          secret: "",
          secret_date: new Date(),
        };

        const api = new ApiHelper({
          baseUrl: import.meta.env.VITE_DATANAC_USERS_API_URL_BASE,
          config: {},
          sessionManager: _sessionManager,
          // No caching:
          _instance: null,
        });
        await api.callPost("system/DocumentSource", _newDocumentSource, globalState);

        setDocumentSources([_newDocumentSource]);
      }
      setIsLoading(false);
    } catch (err) {
      handleError(err);
    }
  };

  useEffect(() => {
    if (currentYear && globalState?.producer_token) {
      loadDocumentSources();
    }
  }, [globalState?.producer_token, activeProducer]);

  useEffect(() => {
    addAttachments([{ name: "Financial Documents Headers", content: Array.isArray(documents) ? documents : [] }]);
  }, [documentSources, documents, addAttachments]);

  // ---

  const [documentSourceEntries, setDocumentSourceEntries] = useState(null);
  const loadDocumentSourceEntries = async () => {
    try {
      setIsloadDocumentSourceEntriesLoading(true);

      const _documentsFilter = {
        producer_token: globalState?.producer_token,
      };
      const _documents = await UsersApiHelper.users_search("DocumentSourceEntry", _documentsFilter, globalState);

      if (_documents?.length) {
        const _sortedDocuments = _documents.sort((a, b) => new Date(b.createdate) - new Date(a.createdate));
        setDocumentSourceEntries(_sortedDocuments);
      }
      setIsloadDocumentSourceEntriesLoading(false);
    } catch (err) {
      handleError(err);
    }
  };

  useEffect(() => {
    loadDocumentSourceEntries();
  }, [globalState?.producer_token]);

  // ---

  const updateDocumentSources = async (event, params, field) => {
    if (params && typeof params === "object" && !Array.isArray(params) && !params.row) {
      params = {
        id: params.id,
        row: { ...params },
      };
    }

    try {
      setIsLoading(true);
      const _documentUpdate = {
        ...params.row,
        producer_token: globalState?.producer_token,
      };
      _documentUpdate[field] = event?.target?.value;

      const api = new ApiHelper({
        baseUrl: import.meta.env.VITE_DATANAC_USERS_API_URL_BASE,
        config: {},
        sessionManager: _sessionManager,
        // No caching:
        _instance: null,
      });
      await api.callPost("system/DocumentSource", _documentUpdate, globalState);

      // Refresh
      loadDocumentSources();

      setIsLoading(false);
    } catch (err) {
      handleError(err);
    }
  };

  // --- --- ---

  const columns = useMemo(
    () => [
      {
        field: "name",
        headerName: "Email Address",
        flex: 1,
        minWidth: 100,
        display: "flex",
        renderCell: (params) => (
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <StatefulControlledTextField
              name={"name"}
              required
              showErrorSpace={false}
              className="datagrid-input"
              size="small"
              defaultValue={params.value || ""}
              onChange={(e) => updateDocumentSources(e, params, "name")}
              InputLabelProps={{ shrink: true }}
            />
            <Typography sx={{ ml: 1 }}>@{DOCUMENT_SOURCE_EMAIL_DOMAIN}</Typography>
          </Box>
        ),
      },
      {
        field: "secret",
        headerName: "Privacy Token",
        flex: 1,
        minWidth: 100,
        display: "flex",
        renderCell: (params) => (
          <StatefulControlledTextField
            name={"secret"}
            required
            showErrorSpace={false}
            className="datagrid-input"
            size="small"
            defaultValue={params.value || ""}
            onChange={(e) => updateDocumentSources(e, params, "secret")}
            InputLabelProps={{ shrink: true }}
          />
        ),
      },
      {
        field: "secret_date",
        headerName: "Last Updated",
        width: 160,
        display: "flex",
        renderCell: (params) => {
          const secretDate = new Date(params.row.secret_date);
          const currentDate = new Date();

          // Calculate the difference in days
          const timeDifference = currentDate - secretDate;
          const daysDifference = Math.floor(timeDifference / (1000 * 60 * 60 * 24)); // Convert milliseconds to days

          // Format the date for display (e.g., 'MMM dd, yyyy')
          const formattedDate = formatDateContext(secretDate);

          return (
            <div
              style={{
                color: daysDifference > DOCUMENT_SOURCE_SECRET_THRESHOLD_DAYS ? "red" : "inherit",
                display: "flex",
                alignItems: "center",
                overflow: "visible",
              }}
            >
              {formattedDate}

              {daysDifference > DOCUMENT_SOURCE_SECRET_THRESHOLD_DAYS && (
                <Tooltip
                  title={`This date is more than ${DOCUMENT_SOURCE_SECRET_THRESHOLD_DAYS} days old`}
                  arrow
                  placement="top"
                >
                  <DangerIcon fontSize="small" color="red" style={{ marginLeft: 8 }} />
                </Tooltip>
              )}
            </div>
          );
        },
      },
      {
        field: "actions",
        headerName: "Actions",
        type: "actions",
        headerAlign: "right",
        align: "right",
        width: 100,
        sortable: false,
        getActions: (params) =>
          params.row.id !== "total"
            ? [
                <GridActionsCellItem
                  key="copy-content"
                  icon={<CopyIcon className="copy-content" />}
                  label="Copy"
                  onClick={() => handleCopy(params?.row.name, params?.row.secret)}
                />,
              ]
            : [],
      },
    ],
    [documents]
  );

  const columnsDocumentSourceEntries = useMemo(
    () => [
      {
        field: "document_name",
        headerName: "File Name",
        flex: 1,
        minWidth: 130,
        display: "flex",
      },
      {
        field: "id",
        headerName: "File Type",
        width: 150,
        display: "flex",
        // Include presigned url for download
        // Iconbutton with download icon
        renderCell: (params) => (
          <div style={{ whiteSpace: "pre-line" }}>
            {getDocumentByDocumentSourceEntry(params.row)?.document_type_name || "Other"}
          </div>
        ),
      },
      {
        field: "notes",
        headerName: "Message",
        flex: 1,
        minWidth: 100,
        display: "flex",
        renderCell: (params) => (
          <div
            style={{
              whiteSpace: "pre-line",
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "-webkit-box",
              WebkitLineClamp: "2",
              WebkitBoxOrient: "vertical",
              marginTop: "6px",
              marginBottom: "10px",
            }}
          >
            <abbr title={params?.value}>{params?.value}</abbr>
          </div>
        ),
      },
      {
        field: "createdate",
        headerName: "Created",
        width: 150,
        display: "flex",
        renderCell: (params) => formatDateContext(params.value),
      },
      {
        field: "actions",
        headerName: "Actions",
        type: "actions",
        headerAlign: "right",
        align: "right",
        width: 100,
        sortable: false,
        getActions: (params) => [
          <GridActionsCellItem
            key="download"
            icon={getFileIcon_DocumentSourceEntry(params.row)}
            label="Download"
            onClick={() => handleDownloadClick_DocumentSource(params.row)}
          />,
        ],
      },
    ],
    [documents, documentSourceEntries, getDocumentByDocumentSourceEntry, getFileIcon_DocumentSourceEntry]
  );

  const renderFilteredCategory = (currentDocumentFilter, index) => {
    return (
      currentDocumentFilter === "All" ||
      (currentDocumentFilter === "Production" && (index === 0 || index === 1)) ||
      (currentDocumentFilter === "Financials" && (index === 2 || index === 3)) ||
      (currentDocumentFilter === "Know Your Customer KYC" && index === 4)
    );
  };

  return (
    <>
      <WorkspaceHeader
        title="Documents"
        category={{
          title: "Farm Vault",
          icon: <FarmVaultIcon />,
        }}
      />

      <Box className="farm-vault-documents" sx={{ maxWidth: "98%" }}>
        <WorkspacePageHeader
          title="Your Documents"
          currentYear={currentYear}
          onYearChange={setCurrentYear}
          viewMode={workspaceMode}
          onViewModeChange={setWorkspaceMode}
          cardGridKey="financial-documents"
          isLoading={isLoading}
          additionalControls={
            <DropdownSelector
              value={currentDocumentFilter}
              onChange={setCurrentDocumentFilter}
              options={documentFilters}
              popupId="documents-filter-selector"
            />
          }
        />

        <Box sx={{ mb: 2, mt: 2 }}>
          <DocumentChatAssistant documents={documents} systemPrompt={DOCUMENT_SYSTEM_PROMPT_NAME_SEARCH} currentYear={currentYear} />
        </Box>

        {isLoading ? (
          <SkeletonTable skeletonTableRows={5} skeletonTableColumn={4} />
        ) : (
          <>
            {isMobile ? (
              <Stack spacing={2} sx={{ width: "100%", maxWidth: 400, m: "0 auto" }}>
                {documentSources?.length > 0 &&
                  documentSources.map((file) => {
                    const secretDate = new Date(file.secret_date);
                    const isValidDate = !isNaN(secretDate.getTime());
                    const now = new Date();
                    const daysDiff = isValidDate ? Math.floor((now - secretDate) / (1000 * 60 * 60 * 24)) : 0;
                    const displayDate = isValidDate ? formatDateContext(secretDate) : "--";
                    return (
                      <Paper key={file.id} elevation={1} sx={{ p: 2, borderRadius: 2 }}>
                        <Stack spacing={1} className="documentSources_mobile">
                          <Box>
                            <Typography variant="subtitle2">Email Address</Typography>
                            <Box className="documentSources_box" sx={{ mt: 2 }}>
                              <StatefulControlledTextField
                                name="name"
                                size="small"
                                defaultValue={file.name || ""}
                                onChange={(e) => updateDocumentSources(e, file, "name")}
                                InputLabelProps={{ shrink: true }}
                                sx={{ flex: 1 }}
                                showErrorSpace={false}
                                required
                              />
                              <Typography sx={{ ml: 1 }}>@{DOCUMENT_SOURCE_EMAIL_DOMAIN}</Typography>
                            </Box>
                          </Box>
                          <Box className="documentSources_box">
                            <Typography variant="subtitle2">Privacy Token</Typography>
                            <StatefulControlledTextField
                              name="secret"
                              size="small"
                              defaultValue={file.secret || ""}
                              onChange={(e) => updateDocumentSources(e, file, "secret")}
                              InputLabelProps={{ shrink: true }}
                              showErrorSpace={false}
                              required
                            />
                          </Box>
                          <Box className="documentSources_box">
                            <Typography variant="subtitle2">Last Updated</Typography>
                            <Box
                              display="flex"
                              alignItems="center"
                              sx={{
                                color:
                                  isValidDate && daysDiff > DOCUMENT_SOURCE_SECRET_THRESHOLD_DAYS
                                    ? "error.main"
                                    : "text.primary",
                              }}
                            >
                              <Typography>{displayDate}</Typography>
                              {isValidDate && daysDiff > DOCUMENT_SOURCE_SECRET_THRESHOLD_DAYS && (
                                <Tooltip
                                  title={`This date is more than ${DOCUMENT_SOURCE_SECRET_THRESHOLD_DAYS} days old`}
                                  arrow
                                  placement="top"
                                >
                                  <WarningAmberOutlinedIcon fontSize="small" sx={{ ml: 1 }} />
                                </Tooltip>
                              )}
                            </Box>
                          </Box>
                          <Box className="documentSources_box">
                            <Typography variant="subtitle2">Actions</Typography>
                            <IconButton onClick={() => handleCopy(file.name, file.secret)} size="small">
                              <CopyIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Stack>
                      </Paper>
                    );
                  })}
              </Stack>
            ) : (
              <TilleyDataGrid
                className="vault-documents"
                rows={documentSources}
                columns={columns}
                pageSizeOptions={[5, 10, 15, 20, 25, 50, 100]}
                defaultPageSize={100}
                gridKey="financial-document-sources"
                getRowId={(row) => row.id}
                rowHeight={80}
                hideFooterPagination
                hideFooterSelectedRowCount
                isLoading={isLoading}
              />
            )}
          </>
        )}
        <Divider />

        {workspaceMode === WORKSPACE_VIEW_TABLE && (
          <FinancialDocumentsWorkspaceTable
            financialDocuments={FINANCIAL_DOCUMENTS}
            currentDocumentFilter={currentDocumentFilter}
            documents={documents}
            isLoading={isLoading}
            handleDownloadClick={handleDownloadClick}
            handleDeleteDocumentClick={handleDeleteDocumentClick}
            setIsUploadFileDialogOpen={setIsUploadFileDialogOpen}
          />
        )}

        {workspaceMode === WORKSPACE_VIEW_CARD && (
          <Stack direction="column">
            {Boolean(FINANCIAL_DOCUMENTS?.length) &&
              _.uniq(FINANCIAL_DOCUMENTS.map((cat) => cat.category)).map(
                (category, index) =>
                  renderFilteredCategory(currentDocumentFilter, index) && (
                    <Box key={index}>
                      <Box sx={{ mb: 1 }}>
                        <Typography sx={{ textTransform: "capitalize", mb: 2, fontSize: "20px", fontWeight: "700" }}>
                          {category}
                        </Typography>

                        <Box className="flexWrapContainer" sx={{ alignItems: "stretch", mb: 2, gap: "18px" }}>
                          {FINANCIAL_DOCUMENTS?.filter((doc) => doc.category === category)?.map(
                            (template, documentIndex) => {
                              const myDocuments = documents?.filter(
                                (doc) => doc.document_type_name === template.document_type_name
                              );

                              // For documents with hideFromWorkspace flag, only show if documents exist
                              if (template.hideFromWorkspace && !myDocuments?.length) {
                                return null;
                              }

                              if (myDocuments?.length) {
                                return myDocuments?.map((currentDocument, currentDocumentIndex) => {
                                  const fileTypeName = getFileTypeName(currentDocument);
                                  <p>{fileTypeName}</p>;
                                  if (fileTypeName !== "Other") {
                                    return (
                                      <Box key={documentIndex * 1000 + currentDocumentIndex}>
                                        <ButtonLargeSquare
                                          dataTestid={currentDocument?.id}
                                          icon={getFileIcon(currentDocument)}
                                          title={currentDocument?.document_type_name}
                                          subHeader={formatDateContext(currentDocument?.lastupdate)}
                                          subHeader2={currentDocument?.file_name}
                                          onClick={() => handleDownloadClick(currentDocument)}
                                          action={() => handleDeleteDocumentClick(currentDocument)}
                                          actionIcon={<DeleteIcon />}
                                        />
                                      </Box>
                                    );
                                  } else {
                                    return (
                                      <Box key={documentIndex * 1000 + currentDocumentIndex}>
                                        <ButtonLargeSquare
                                          dataTestid={currentDocument?.id}
                                          sx={{ backgroundColor: "grey.200" }}
                                          icon={getFileIcon(currentDocument)}
                                          title={template?.document_type_name}
                                          subHeader2={getTruncatedFilename(template.templateFileName, 30) || ""}
                                          subHeader={
                                            formatDateContext(currentDocument?.lastupdate) || "Entered Manually"
                                          }
                                          action={() => handleDeleteDocumentClick(currentDocument)}
                                          actionIcon={<DeleteIcon />}
                                        />
                                      </Box>
                                    );
                                  }
                                });
                              } else {
                                if (isLoading) {
                                  return (
                                    <Box key={documentIndex}>
                                      <ButtonLargeSquare
                                        sx={{}}
                                        icon={
                                          <TimerIcon
                                            className="loadingIcon"
                                            sx={{ color: "black", width: "1.5em", height: "1.5em" }}
                                          />
                                        }
                                        title={template?.document_type_name}
                                        subHeader={"Loading..."}
                                      />
                                    </Box>
                                  );
                                } else {
                                  return (
                                    <Box key={documentIndex}>
                                      <ButtonLargeSquare
                                        sx={{}}
                                        icon={
                                          <DangerIcon
                                            sx={{ fontSize: "3rem", color: "palette.text.disabled", opacity: 0.7 }}
                                          />
                                        }
                                        title={template?.document_type_name}
                                        subHeader={template?.subHeader || ""}
                                        disabled={false} // previously it was true
                                        action={() => setIsUploadFileDialogOpen(true)}
                                        actionIcon={<></>}
                                        actionButton={true}
                                        actionButtonText="Upload File"
                                      />
                                    </Box>
                                  );
                                }
                              }
                            }
                          )}
                        </Box>
                      </Box>
                    </Box>
                  )
              )}
            <br />
          </Stack>
        )}
        <Divider />
        <Stack direction="row" columnGap={2} sx={{ pt: 1.5 }}>
          <Typography variant="h1">@ToTilley.com</Typography>
          <IconButton
            className="hiddenButton"
            size="large"
            onClick={() => {
              loadDocumentSourceEntries();
            }}
          >
            <Refresh size="large" />
          </IconButton>
        </Stack>
        {isloadDocumentSourceEntriesLoading ? (
          <SkeletonTable skeletonTableRows={5} skeletonTableColumn={4} />
        ) : (
          <>
            {isMobile ? (
              <Stack spacing={2} sx={{ width: "100%", maxWidth: 400, m: "0 auto" }}>
                {documentSourceEntries?.length > 0 ? (
                  documentSourceEntries.map((file) => (
                    <Paper key={file.id} elevation={1} sx={{ p: 2, borderRadius: 2 }}>
                      <Stack spacing={1} className="documentSources_mobile">
                        <Box className="documentSources_box">
                          <Typography variant="subtitle2">File Name</Typography>
                          <Typography>{file.document_name}</Typography>
                        </Box>
                        <Box className="documentSources_box">
                          <Typography variant="subtitle2">File Type</Typography>
                          <Typography style={{ whiteSpace: "pre-line" }}>
                            {file.document_type_name || "Other"}
                          </Typography>
                        </Box>
                        <Box className="documentSources_box">
                          <Typography variant="subtitle2">Message</Typography>
                          <div
                            style={{
                              whiteSpace: "pre-line",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              display: "-webkit-box",
                              WebkitLineClamp: "2",
                              WebkitBoxOrient: "vertical",
                              marginTop: 6,
                              marginBottom: 10,
                            }}
                          >
                            <abbr title={file.notes}>{file.notes}</abbr>
                          </div>
                        </Box>
                        <Box className="documentSources_box">
                          <Typography variant="subtitle2">Created</Typography>
                          <Typography>{formatDateContext(file.createdate)}</Typography>
                        </Box>
                        <Box className="documentSources_box">
                          <Typography variant="subtitle2">Actions</Typography>
                          <IconButton onClick={() => handleDownloadClick_DocumentSource(file)}>
                            <DownloadIcon sx={{ fontSize: "1.5em" }} />
                          </IconButton>
                        </Box>
                      </Stack>
                    </Paper>
                  ))
                ) : (
                  <Paper elevation={1} sx={{ p: 2, borderRadius: 2 }}>
                    <Typography variant="body1" sx={{ textAlign: "center", mt: 4, mb: 4 }}>
                      No data found.
                    </Typography>
                  </Paper>
                )}
              </Stack>
            ) : (
              <TilleyDataGrid
                rows={documentSourceEntries || []}
                columns={columnsDocumentSourceEntries}
                getRowHeight={getRowHeightAuto}
                pageSizeOptions={[5, 10, 15, 20, 25, 50, 100]}
                defaultPageSize={isMobile ? 5 : 10}
                gridKey="financial-document-source-entries"
                getRowId={(row) => row.id}
                isLoading={isloadDocumentSourceEntriesLoading}
              />
            )}
          </>
        )}
        <Divider />
        <Typography variant="h1">Templates</Typography>
        <Stack direction="column">
          <Box sx={{ mb: 2 }}>
            <Box className="flexWrapContainer" sx={{ alignItems: "stretch", gap: "18px" }}>
              {FINANCIAL_DOCUMENTS?.filter(
                (doc) => doc.templateFileName && FINANCIAL_DOCUMENTS_HAVE_TEMPLATES.includes(doc.title)
              )?.map((d, index) => (
                <Box key={index}>
                  <ButtonLargeSquare
                    icon={
                      <>
                        {d.icon ? <d.icon sx={{ fontSize: "3rem" }} /> : <EditNoteOutlined sx={{ fontSize: "3rem" }} />}
                      </>
                    }
                    title={d.title}
                    subHeader={d.subHeader || " "}
                    onClick={() => handlePublicDownloadClick(d)}
                    dataTestid={`${d.title} template`}
                  />
                </Box>
              ))}
            </Box>
          </Box>
        </Stack>
      </Box>

      <WorkspaceActionBar
        content={
          <>
            <Stack direction="row" spacing={2}>
              <PopupState variant="popover" popupId="file-menu-popper">
                {() => (
                  <>
                    <Button
                      variant="contained"
                      onClick={() => {
                        setIsUploadFileDialogOpen(true);
                      }}
                      data-testid="add-document"
                    >
                      <UploadFileOutlined sx={{ mr: 1 }} />
                      Add Document
                    </Button>
                  </>
                )}
              </PopupState>
            </Stack>
          </>
        }
      />

      <Dialog
        className={`${isMobile && "document-file-upload-dialoug-mobile"} document-file-upload-dialoug`}
        open={isUploadFileDialogOpen}
        fullWidth={isSmall}
        PaperProps={{ sx: {} }}
      >
        <ImportUploadFileDialog
          year={currentYear}
          onClose={() => {
            setIsUploadFileDialogOpen(false);
            loadDocuments();
          }}
        />
      </Dialog>

      {confirmDialogComponent}
    </>
  );
}
